import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function GetEditorAddedTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdminNotesModal, setShowAdminNotesModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    header: {
      padding: '30px 20px 20px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    backBtn: {
      background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 20px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    content: {
      padding: '0 20px 40px',
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    filtersContainer: {
      display: 'flex',
      gap: '15px',
      marginBottom: '20px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    filterBtn: {
      background: 'rgba(255, 255, 255, 0.07)',
      color: colors.textLight,
      border: `1px solid ${colors.borderDark}`,
      padding: '8px 20px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    activeFilterBtn: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: `1px solid ${colors.primary}`,
      padding: '8px 20px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    searchInput: {
      padding: '8px 15px',
      background: 'rgba(255, 255, 255, 0.07)',
      border: `1px solid ${colors.borderDark}`,
      borderRadius: '8px',
      color: 'white',
      fontSize: '0.9rem',
      minWidth: '250px',
      backdropFilter: 'blur(10px)'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '25px',
      borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    statsContainer: {
      display: 'flex',
      gap: '15px',
      marginTop: '15px',
      flexWrap: 'wrap'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '12px 20px',
      borderRadius: '10px',
      border: `1px solid ${colors.borderDark}`,
      minWidth: '120px'
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: 'white',
      margin: 0
    },
    statLabel: {
      fontSize: '0.8rem',
      color: colors.textLight,
      margin: '5px 0 0 0',
      opacity: 0.8
    },
    tableContainer: {
      overflowX: 'auto',
      padding: '20px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '1400px'
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '12px 15px',
      fontWeight: '600',
      whiteSpace: 'nowrap',
      fontSize: '0.85rem',
      textAlign: 'left'
    },
    tableRow: {
      borderBottom: `1px solid ${colors.borderDark}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.03)'
      }
    },
    tableCell: {
      padding: '12px 15px',
      verticalAlign: 'middle',
      fontSize: '0.85rem',
      color: colors.textLight
    },
    emptyState: {
      padding: '60px 20px',
      textAlign: 'center'
    },
    emptyIcon: {
      fontSize: '3rem',
      color: `${colors.textLight}30`,
      marginBottom: '15px'
    },
    emptyTitle: {
      color: colors.textLight,
      fontSize: '1.2rem',
      marginBottom: '10px'
    },
    emptyText: {
      color: colors.textLight,
      fontSize: '0.9rem',
      opacity: 0.7
    },
    messageAlert: {
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 auto 25px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1400px',
      width: '100%',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
    },
    loadingState: {
      padding: '60px 20px',
      textAlign: 'center'
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: `3px solid ${colors.borderDark}`,
      borderTop: `3px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px'
    },
    statusBadge: {
      padding: '5px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-block',
      textAlign: 'center',
      minWidth: '90px'
    },
    editBtn: {
      background: colors.primary,
      color: 'white',
      border: 'none',
      padding: '6px 15px',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      '&:hover': {
        background: colors.secondary,
        transform: 'translateY(-2px)'
      }
    },
    dateCell: {
      fontSize: '0.8rem',
      color: `${colors.textLight}90`
    },
    videoTypeBadge: {
      background: colors.accent + '20',
      color: colors.accent,
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-block'
    },
    videoCountBadge: {
      background: colors.primary + '20',
      color: colors.primary,
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-block'
    },
    thumbnailBtn: {
      background: `linear-gradient(135deg, ${colors.accent}, #22D3EE)`,
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.7rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100px'
    },
    adminNotesBtn: {
      background: `rgba(255, 255, 255, 0.1)`,
      color: colors.textLight,
      border: `1px solid ${colors.borderDark}`,
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.7rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      width: '100%',
      justifyContent: 'center',
      marginTop: '4px'
    }
  };

  useEffect(() => {
    fetchEditorAddedTasks();
  }, []);

  const fetchEditorAddedTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      // Get the editor ID from session storage
      const editorId = sessionStorage.getItem('userId');
      
      if (!editorId) {
        throw new Error('Editor ID not found. Please log in again.');
      }
      
      const response = await fetch(`${API_BASE}/get_editor_added_tasks.php?editor_id=${editorId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        let tasksData = [];
        
        if (Array.isArray(data.tasks)) {
          tasksData = data.tasks;
        } else if (Array.isArray(data.data)) {
          tasksData = data.data;
        } else {
          throw new Error('Invalid tasks data format received from server');
        }

        console.log('Fetched editor added tasks:', tasksData);
        setTasks(tasksData);
      } else {
        throw new Error(data.message || 'Failed to fetch tasks');
      }
      
    } catch (error) {
      console.error('❌ Error fetching editor added tasks:', error);
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks based on selected filter and search term
  const filteredTasks = tasks.filter(task => {
    // Apply status filter
    if (filter !== 'all' && task.status !== filter) {
      return false;
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        (task.video_name && task.video_name.toLowerCase().includes(searchLower)) ||
        (task.client_name && task.client_name.toLowerCase().includes(searchLower)) ||
        (task.assigned_by_name && task.assigned_by_name.toLowerCase().includes(searchLower)) ||
        (task.notes && task.notes.toLowerCase().includes(searchLower)) ||
        (task.video_type && task.video_type.toLowerCase().includes(searchLower)) ||
        (task.admin_notes && task.admin_notes.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  // Calculate statistics
  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    todo: tasks.filter(t => t.status === 'todo').length,
    assigned: tasks.filter(t => t.status === 'assigned').length
  };

  const getStatusBadgeStyle = (status) => {
    const statusConfig = {
      pending: { bg: colors.warning, text: '#000' },
      todo: { bg: colors.warning, text: '#000' },
      in_progress: { bg: colors.accent, text: '#fff' },
      completed: { bg: colors.success, text: '#fff' },
      assigned: { bg: colors.primary, text: '#fff' }
    };
    
    const config = statusConfig[status] || { bg: colors.secondary, text: '#fff' };
    
    return {
      background: config.bg,
      color: config.text,
      ...styles.statusBadge
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null' || text.trim() === '') return '-';
    const cleanText = String(text).trim();
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  const handleEditTask = (task) => {
    // Navigate to edit page with task data
    navigate(`/edit-editor-task/${task.id}`, { 
      state: { 
        task: task,
        isEditMode: true 
      } 
    });
  };

  const handleViewRawLink = (link) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    } else {
      setMessage('❌ No video link available');
    }
  };

  const openLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleViewAdminNotes = (task) => {
    setSelectedTask(task);
    setAdminNotes(task.admin_notes || '');
    setShowAdminNotesModal(true);
  };

  const closeAdminNotesModal = () => {
    setShowAdminNotesModal(false);
    setSelectedTask(null);
    setAdminNotes('');
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <h3 style={{color: colors.textLight, marginBottom: '10px'}}>Loading Editor Tasks</h3>
            <p style={{color: colors.textLight, opacity: 0.7}}>Fetching tasks added by operators...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                <button 
                  style={styles.backBtn}
                  onClick={() => navigate('/my-tasks')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back to My Tasks
                </button>
                <h1 style={styles.pageTitle}>
                  <i className="fas fa-tasks"></i>
                  Editor Tasks Pool
                </h1>
              </div>
            </div>
            <p style={{color: colors.textLight, marginTop: '10px', opacity: 0.8, maxWidth: '800px'}}>
              Tasks added by operators for editors to pick up. Available tasks that need to be assigned.
            </p>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? `linear-gradient(135deg, ${colors.success}20, ${colors.success}10)` 
              : `linear-gradient(135deg, ${colors.danger}20, ${colors.danger}10)`,
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`
          }}>
            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`} 
               style={{
                 fontSize: '1rem',
                 color: message.includes('✅') ? colors.success : colors.danger
               }}></i>
            <span style={{flex: 1, fontWeight: '500'}}>{message}</span>
            <button onClick={() => setMessage('')} style={{
              background: 'none', 
              border: 'none', 
              color: colors.danger, 
              cursor: 'pointer', 
              padding: '5px',
              borderRadius: '5px'
            }}>
              <i className="fas fa-times" style={{fontSize: '0.9rem'}}></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          {/* Filters and Search */}
          <div style={styles.filtersContainer}>
            <button 
              style={filter === 'all' ? styles.activeFilterBtn : styles.filterBtn}
              onClick={() => setFilter('all')}
            >
              All Tasks ({stats.total})
            </button>
            <button 
              style={filter === 'pending' ? styles.activeFilterBtn : styles.filterBtn}
              onClick={() => setFilter('pending')}
            >
              Pending ({stats.pending})
            </button>
          
            <button 
              style={filter === 'in_progress' ? styles.activeFilterBtn : styles.filterBtn}
              onClick={() => setFilter('in_progress')}
            >
              In Progress ({stats.in_progress})
            </button>
            <button 
              style={filter === 'completed' ? styles.activeFilterBtn : styles.filterBtn}
              onClick={() => setFilter('completed')}
            >
              Completed ({stats.completed})
            </button>
            
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            
            <button 
              style={styles.filterBtn}
              onClick={fetchEditorAddedTasks}
            >
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
          </div>

          {/* Tasks Card */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-list-check"></i>
                Available Tasks for Editors
                <span style={{fontSize: '0.9rem', opacity: 0.9, marginLeft: '10px'}}>
                  {filteredTasks.length} of {tasks.length} tasks
                </span>
              </h3>
            </div>

            <div style={styles.tableContainer}>
              {filteredTasks.length === 0 ? (
                <div style={styles.emptyState}>
                  <i className="fas fa-inbox" style={styles.emptyIcon}></i>
                  <h3 style={styles.emptyTitle}>No tasks found</h3>
                  <p style={styles.emptyText}>
                    {searchTerm || filter !== 'all' 
                      ? 'Try changing your search or filter criteria' 
                      : 'No tasks have been added by operators yet'}
                  </p>
                  {searchTerm && (
                    <button 
                      style={{
                        ...styles.filterBtn,
                        marginTop: '15px'
                      }}
                      onClick={() => {
                        setSearchTerm('');
                        setFilter('all');
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>ID</th>
                      <th style={styles.tableHeader}>Video Name</th>
                      <th style={styles.tableHeader}>Client</th>
                      <th style={styles.tableHeader}>Added By</th>
                      <th style={styles.tableHeader}>Type</th>
                      <th style={styles.tableHeader}>Count</th>
                      <th style={styles.tableHeader}>Status</th>
                      <th style={styles.tableHeader}>Thumbnail</th>
                      <th style={styles.tableHeader}>Admin Notes</th>
                      <th style={styles.tableHeader}>Date Added</th>
                      <th style={styles.tableHeader}>Shoot Date</th>
                      <th style={styles.tableHeader}>Video Link</th>
                      <th style={styles.tableHeader}>Notes</th>
                      <th style={styles.tableHeader}>Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((task) => (
                      <tr key={task.id} style={styles.tableRow}>
                        {/* ID */}
                        <td style={{...styles.tableCell, fontWeight: 'bold', color: colors.primary}}>
                          #{task.id}
                        </td>

                        {/* Video Name */}
                        <td style={styles.tableCell}>
                          <div style={{fontWeight: '600', color: 'white'}}>
                            {task.video_name || '-'}
                          </div>
                        </td>

                        {/* Client */}
                        <td style={styles.tableCell}>
                          {task.client_name || 'No Client'}
                        </td>

                        {/* Added By */}
                        <td style={styles.tableCell}>
                          <div style={{fontWeight: '500'}}>{task.assigned_by_name || 'Unknown'}</div>
                          <div style={styles.dateCell}>{formatDate(task.assigned_at)}</div>
                        </td>

                        {/* Video Type */}
                        <td style={styles.tableCell}>
                          <span style={styles.videoTypeBadge}>
                            {task.video_type?.replace('_', ' ') || 'Standard'}
                          </span>
                        </td>

                        {/* Video Count */}
                        <td style={styles.tableCell}>
                          <span style={styles.videoCountBadge}>
                            {task.video_count || 1}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={styles.tableCell}>
                          <span style={getStatusBadgeStyle(task.status)}>
                            {task.status?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </td>

                        {/* Thumbnail */}
                        <td style={styles.tableCell}>
                          {task.thumbnail_link ? (
                            <button
                              onClick={() => openLink(task.thumbnail_link)}
                              style={styles.thumbnailBtn}
                              title={task.thumbnail_link}
                            >
                              <i className="fas fa-external-link-alt"></i>
                              Thumbnail
                            </button>
                          ) : (
                            <span style={{ 
                              color: colors.textLight, 
                              fontSize: '0.8rem',
                              fontStyle: 'italic'
                            }}>
                              No thumbnail
                            </span>
                          )}
                        </td>

                        {/* Admin Notes */}
                        <td style={styles.tableCell}>
                          <div style={{
                            maxHeight: '60px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: '1.2',
                            color: colors.textLight,
                            fontSize: '0.8rem'
                          }}>
                            {task.admin_notes || 'No admin notes'}
                          </div>
                          {task.admin_notes && (
                            <button
                              onClick={() => handleViewAdminNotes(task)}
                              style={styles.adminNotesBtn}
                              title="View Admin Notes"
                            >
                              <i className="fas fa-eye"></i>
                              View Notes
                            </button>
                          )}
                        </td>

                        {/* Date Added */}
                        <td style={styles.tableCell}>
                          {formatDateTime(task.created_at)}
                        </td>

                        {/* Shoot Date */}
                        <td style={styles.tableCell}>
                          {task.shoot_date ? formatDate(task.shoot_date) : '-'}
                        </td>

                        {/* Video Link */}
                        <td style={styles.tableCell}>
                          {task.raw_link && task.raw_link !== '-' ? (
                            <button
                              onClick={() => handleViewRawLink(task.raw_link)}
                              style={{
                                background: colors.accent,
                                color: 'white',
                                border: 'none',
                                padding: '5px 10px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={task.raw_link}
                            >
                              <i className="fas fa-external-link-alt"></i> Open
                            </button>
                          ) : (
                            <span style={{opacity: 0.5}}>-</span>
                          )}
                        </td>

                        {/* Notes */}
                        <td style={styles.tableCell}>
                          <div title={task.notes || ''}>
                            {truncateText(task.notes, 15)}
                          </div>
                        </td>

                        {/* Edit Button */}
                        <td style={styles.tableCell}>
                          <button
                            style={styles.editBtn}
                            onClick={() => handleEditTask(task)}
                            title="Edit Task"
                          >
                            <i className="fas fa-edit"></i> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Notes Modal */}
      {showAdminNotesModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 100%)`,
            borderRadius: '15px',
            border: `1px solid ${colors.borderDark}`,
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.info}, #3B82F6)`,
              color: 'white',
              padding: '20px',
              borderRadius: '15px 15px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-sticky-note"></i>
                Admin Notes - Task #{selectedTask.id}
              </h3>
              <button
                onClick={closeAdminNotesModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              {/* Task Info */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <div>
                    <span style={{ 
                      color: colors.textLight, 
                      fontSize: '0.85rem',
                      display: 'block'
                    }}>
                      Task #{selectedTask.id}
                    </span>
                    <span style={{ 
                      color: 'white', 
                      fontWeight: '500',
                      fontSize: '1.1rem'
                    }}>
                      {selectedTask.video_name}
                    </span>
                  </div>
                  <div>
                    <span style={getStatusBadgeStyle(selectedTask.status)}>
                      {selectedTask.status?.replace('_', ' ') || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div style={{ 
                  color: colors.textLight, 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-user-tie"></i>
                  Client: {selectedTask.client_name || 'No client'}
                </div>
              </div>

              {/* Admin Notes Display */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-sticky-note" style={{ marginRight: '8px' }}></i>
                  Admin Notes
                </label>
                <div style={{
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.07)',
                  border: `1px solid ${colors.borderDark}`,
                  borderRadius: '8px',
                  color: colors.textLight,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem',
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  lineHeight: '1.5'
                }}>
                  {adminNotes || 'No admin notes available for this task.'}
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: colors.textLight, 
                  marginTop: '8px',
                  opacity: 0.7,
                  fontStyle: 'italic'
                }}>
                  <i className="fas fa-info-circle"></i> 
                  These notes are from the admin/manager for this task.
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '20px'
              }}>
                <button
                  onClick={closeAdminNotesModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: colors.textLight,
                    border: `1px solid ${colors.borderDark}`,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-times"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
}
export default GetEditorAddedTasks;